import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { FormActor, FormArticle, FormArticleByKey, FormDefinition, FormSubscription } from 'app/shared/models/class/forms/form-definition';
import { Observable, Subject } from 'rxjs';
import { environment } from 'environments/environment';
import * as FileSaver from 'file-saver';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class FormService {

  private readonly api_url_form = environment.apiUrl + '/Form';
  private readonly subscriptionAddedStorageKey = 'formSubscriptionAdded';

  isSubscriptionAdded$ = new Subject<boolean>();

  constructor(private readonly http: HttpClient) {
    // the subscription-form is opened in a separate browser tab, so cross-tab notifications
    // are relayed through localStorage (the 'storage' event never fires on the tab that wrote the value)
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === this.subscriptionAddedStorageKey && event.newValue) {
        this.isSubscriptionAdded$.next(true);
      }
    });
  }

  notifySubscriptionAdded(): void {
    this.isSubscriptionAdded$.next(true);
    localStorage.setItem(this.subscriptionAddedStorageKey, Date.now().toString());
  }

  // Form definition
  getFormDefinition(filter?: any): Observable<FormDefinition[]> {
    let params = new HttpParams();
    if (filter?.key) { params = params.append('Key', filter.key); }
    if (filter?.enabled) { params = params.append('Enabled', filter.enabled); }
    if (filter?.actorKey) { params = params.append('ActorKey', filter.actorKey); }
    if (filter?.articleKey) { params = params.append('ArticleKey', filter.articleKey); }

    return this.http.get<FormDefinition[]>(`${this.api_url_form}/definition`, { params });
  }

  getFormDefinitionByKey(key: number): Observable<FormDefinition> {
    return this.http.get<FormDefinition>(`${this.api_url_form}/definition/${key}`);
  }

  postFormDefinition(formDefinition: FormDefinition): any {
    return this.http.post(`${this.api_url_form}/definition`, formDefinition);
  }

  putFormDefinition(formDefinition: FormDefinition): any {
    return this.http.put(`${this.api_url_form}/definition`, formDefinition);
  }

  deleteFormDefinition(key: number): any {
    return this.http.delete(`${this.api_url_form}/definition/${key}`);
  }

  // Form article

  getFormDefinitionByArticleKey(articleKey: number): Observable<FormArticleByKey> {
    return this.http.get<FormArticleByKey>(`${this.api_url_form}/article/${articleKey}`);
  }
  postFormArticle(formArticle: FormArticle): any {
    return this.http.post(`${this.api_url_form}/article`, formArticle);
  }

  putFormArticle(formArticle: FormArticle): any {
    return this.http.put(`${this.api_url_form}/article`, formArticle);
  }

  deleteFormArticle(key: string): any {
    return this.http.delete(`${this.api_url_form}/article/${key}`);
  }

  getFormReportByArticleKey(articleKey: number): Observable<Blob> {
    return this.http.get(`${this.api_url_form}/report/article/${articleKey}`, { observe: 'response', responseType: 'blob' })
                .pipe(map(res => this.saveFile(res)));
  }

  // Form actor
  postFormActor(formActor: FormActor): any {
    return this.http.post(`${this.api_url_form}/actor`, formActor);
  }

  putFormActor(formActor: FormActor): any {
    return this.http.put(`${this.api_url_form}/actor`, formActor);
  }

  deleteFormActor(key: string): any {
    return this.http.delete(`${this.api_url_form}/actor/${key}`);
  }

  // Form subscription
  getFormSubscription(filter?: any): Observable<FormSubscription[]> {
    let params = new HttpParams();
    if (filter?.key) { params = params.append('Key', filter.key); }
    if (filter?.articleKey) { params = params.append('ArticleKey', filter.articleKey); }
    if (filter?.actorKey) { params = params.append('ActorKey', filter.actorKey); }
    if (filter?.formDefinitionKey) { params = params.append('FormDefinitionKey', filter.formDefinitionKey); }

    return this.http.get<FormSubscription[]>(`${this.api_url_form}/subscription`, { params });
  }

  getFormSubscriptionByKey(key: number): Observable<FormSubscription> {
    return this.http.get<FormSubscription>(`${this.api_url_form}/subscription/${key}`);
  }

  postFormSubscription(formSubscription: FormSubscription): any {
    return this.http.post(`${this.api_url_form}/subscription`, formSubscription);
  }

  putFormSubscription(formSubscription: FormSubscription): any {
    return this.http.put(`${this.api_url_form}/subscription`, formSubscription);
  }

  deleteFormSubscription(key: number): any {
    return this.http.delete(`${this.api_url_form}/subscription/${key}`);
  }

  private saveFile(res: HttpResponse<Blob>): Blob {
    FileSaver.saveAs(res.body!, res.headers.get('filename')!);
    return res.body!;
  }

}
